/**
 * @file LabelTemplateSegmented.tsx
 * @description Выбор шаблона складской этикетки сегментами с пиктограммой
 * (phase19.22, W17-5b): Стандарт 70×37 / Компакт 52.5×29.7 / Мини 20×20.
 *
 * Три варианта с разной формой — сегменты нагляднее списка: пиктограмма
 * показывает пропорции наклейки. Подписи и размеры — из реестра
 * `LABEL_TEMPLATE_META`, новый шаблон = запись там. Тач-цель ≥ 48px; на 360
 * сегменты делят ширину поровну.
 */

import { cn } from "@/lib/utils";
import type { LabelTemplate } from "../../../lib/api-product-kinds";
import { LABEL_TEMPLATES, LABEL_TEMPLATE_META } from "../../../lib/product-kind-format";

interface Props {
  value: LabelTemplate;
  onChange: (value: LabelTemplate) => void;
  disabled?: boolean;
  ariaLabel?: string;
}

export function LabelTemplateSegmented({
  value,
  onChange,
  disabled,
  ariaLabel = "Шаблон этикетки",
}: Props) {
  return (
    <div role="radiogroup" aria-label={ariaLabel} className="grid grid-cols-3 gap-2">
      {LABEL_TEMPLATES.map((template) => {
        const meta = LABEL_TEMPLATE_META[template];
        const checked = value === template;
        return (
          <button
            key={template}
            type="button"
            role="radio"
            aria-checked={checked}
            disabled={disabled}
            onClick={() => onChange(template)}
            className={cn(
              "flex min-h-12 flex-col items-center justify-center gap-1 rounded-xl px-2 py-2 text-center transition-colors",
              "outline-none focus-visible:ring-2 focus-visible:ring-ring",
              checked
                ? "bg-primary/10 text-on-surface ring-1 ring-primary/40"
                : "bg-surface-low text-on-surface hover:bg-surface-low/70",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
          >
            {/* Пиктограмма: прямоугольник в пропорциях наклейки. */}
            <span
              aria-hidden
              className={cn(
                "rounded-[3px] border-2",
                checked ? "border-primary" : "border-on-surface-variant/60",
              )}
              style={{ width: meta.icon.w, height: meta.icon.h }}
            />
            <span className="text-sm font-medium">{meta.label}</span>
            <span className="text-xs text-on-surface-variant">{meta.size}</span>
          </button>
        );
      })}
    </div>
  );
}
